import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PresidioNotificationItem } from 'src/app/extensions/presidio-demo/models/presidio-notification.model';
import { PresidioNotificationsFacade } from 'src/app/extensions/presidio-demo/services/presidio-notifications.facade';

import { PresidioShellUiFacade } from './presidio-shell-ui.facade';

/**
 * CUSTOMIZATION: Header notifications bell — actionable feed for quotes, pricing,
 * orders, approvals, and home “Needs your attention” items (MVP 3.2.7 + 1.2.4 / 1.2.6).
 */
@Component({
  selector: 'custom-notifications-menu',
  templateUrl: './custom-notifications-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomNotificationsMenuComponent implements OnInit, OnDestroy {
  open = false;
  items$: Observable<PresidioNotificationItem[]>;
  unreadCount$: Observable<number>;

  private destroy$ = new Subject<void>();

  constructor(
    private host: ElementRef<HTMLElement>,
    private shellUi: PresidioShellUiFacade,
    private notifications: PresidioNotificationsFacade,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    void this.notifications.ensureLoaded();
    this.items$ = this.notifications.items$;
    this.unreadCount$ = this.notifications.unreadCount$;

    this.shellUi.notificationsOpen$.pipe(takeUntil(this.destroy$)).subscribe(open => {
      this.open = open;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(): void {
    const next = !this.open;
    this.shellUi.setNotificationsOpen(next);
    if (next) {
      this.notifications.trackPanelOpen();
    }
  }

  close(): void {
    this.shellUi.closeNotifications();
  }

  async onSelect(item: PresidioNotificationItem, event?: Event): Promise<void> {
    // Close destroys the panel (*ngIf) before routerLink can navigate — drive nav explicitly.
    event?.preventDefault();
    await this.notifications.selectNotification(item);
    this.close();
    await this.router.navigateByUrl(item.link);
  }

  async toggleRead(item: PresidioNotificationItem, event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    await this.notifications.setRead(item, !item.read);
  }

  async markAllRead(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    await this.notifications.markAllRead();
  }

  async clearAll(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    await this.notifications.clearAll();
  }

  categoryIcon(category: PresidioNotificationItem['category']): string {
    switch (category) {
      case 'quote_received':
      case 'quote_expiring':
        return 'bi-file-earmark-text';
      case 'pricing_request':
        return 'bi-currency-dollar';
      case 'order_placed':
        return 'bi-bag-check';
      case 'order_approval':
        return 'bi-clipboard-check';
      case 'user_approval':
        return 'bi-person-check';
      case 'invoice_due':
        return 'bi-receipt';
      default:
        return 'bi-bell';
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.close();
    }
  }
}
